import React, { useEffect, useRef, useState } from 'react';
import { gantt } from 'dhtmlx-gantt';
// import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import Button from 'components/base/Button';

import { ButtonGroup } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { getPlannerWeekRange } from 'helpers/utils';

// --- Constants (Extracted Magic Numbers) ---
const DAY_START_HOUR = 8;
const DAY_END_HOUR = 19;
const WEEK_START_HOUR = 0;
// const WEEK_END_HOUR = 24; // 24:00 of the last day
const WORK_HOURS = [`${DAY_START_HOUR}:00`, `${DAY_END_HOUR}:00`];
const WEEK_DAYS_TO_SHOW = 5; // Monday to Friday

// --- Interfaces (Updated for Passenger View) ---
// Trip now represents any task (confirmed or unassigned request)
export interface Trip {
  organizer: string;
  id: string | number;
  companiesList: string;
  user: number | undefined;
  mainPassenger: string; // Used for grouping
  listPassenger: string;
  outsideUsers: number;
  grupo: number;
  start_date: Date;
  end_date: Date;
  duration: number;
  carPlate?: string | null; // Optional: null/undefined for unassigned requests
  originalStart: Date;
  originalEnd: Date;
  takecar: string;
  leavecar: string;
  isClipped: boolean;
  isConfirmed: boolean; // New flag to distinguish confirmed from unassigned
  status: string | undefined;
  statusToSet: string | undefined;
}

interface GanttTask {
  id: string | number;
  text: string;
  open?: boolean;
  render?: string;
  parent?: string;
  start_date?: Date;
  end_date?: Date;
  duration?: number;
  passenger?: string;
  carPlate?: string | null;
  originalStart?: Date;
  originalEnd?: Date;
  isClipped?: boolean;
  isPending?: boolean;
  isConfirmed?: boolean; // New flag
  type?: string;
  status?: string;
  statusToSet?: string;
}

interface GanttData {
  data: GanttTask[];
  links: [];
}

export interface GanttChartProps {
  trips: Trip[];
  pendingTripIds?: number[];
  initialDate?: Date;
  initialViewMode?: 'day' | 'week';
  onPlannerDateChange?: (date: Date) => void;
  onCancelTrip?: (tripId: number) => void;
  onVisibleTripsChange?: (tripIds: number[]) => void;
  onVisibleWeeklyTripsChange?: (tripIds: number[]) => void;
  // ✨ NEW PROP: Handler for assigning a car to an unassigned request
  onAssignCar?: (tripId: number) => void;
}

// --- Consolidated Helper Functions ---

/**
 * Clips a single trip to a given time boundary (day or week).
 * This function MUST be called after gantt.init() and configuration.
 */
const clipTrip = (
  trip: Trip,
  startBoundary: Date,
  endBoundary: Date
): Trip | null => {
  const tripStart = new Date(trip.start_date);
  const tripEnd = new Date(
    tripStart.getTime() + trip.duration * 60 * 60 * 1000
  );

  const overlaps = tripStart < endBoundary && tripEnd > startBoundary;
  if (!overlaps) return null;

  const visibleStart = new Date(
    Math.max(tripStart.getTime(), startBoundary.getTime())
  );
  const visibleEnd = new Date(
    Math.min(tripEnd.getTime(), endBoundary.getTime())
  );

  const visibleDuration = gantt.calculateDuration({
    start_date: visibleStart,
    end_date: visibleEnd
  });

  const isClipped = tripStart < startBoundary || tripEnd > endBoundary;

  return {
    ...trip,
    start_date: visibleStart,
    end_date: visibleEnd,
    duration: visibleDuration,
    originalStart: tripStart,
    originalEnd: tripEnd,
    isClipped: isClipped,
    id: trip.id
  };
};

/**
 * Transforms trips into Gantt data, grouping by passenger.
 * Unassigned trips (no carPlate) are now tasks under the passenger group.
 */
const transformTripsToGanttData = (
  trips: Trip[],
  pendingIds: Set<string | number>
): GanttData => {
  // Group by passenger
  const passengerMap = new Map<string, string>();
  const ganttData: GanttTask[] = [];

  trips.forEach(trip => {
    const passengerKey = trip.mainPassenger;
    if (!passengerMap.has(passengerKey)) {
      const passengerId = `passenger-${passengerKey.replace(/\s/g, '_')}`;
      passengerMap.set(passengerKey, passengerId);

      // Create a parent task for the passenger/group
      ganttData.push({
        id: passengerId,
        text: passengerKey, // Passenger name is the row label
        open: true,
        type: 'project',
        render: 'split'
      });
    }
  });

  trips.forEach(trip => {
    ganttData.push({
      ...trip,
      parent: passengerMap.get(trip.mainPassenger),
      isPending: pendingIds.has(trip.id),
      // The task text now includes the car plate if assigned, or a status if unassigned
      text: trip.carPlate
        ? `${trip.carPlate} - ${trip.companiesList} `
        : trip.status === 'Recusado'
          ? trip.status
          : `${trip.companiesList}`
    });
  });

  return {
    data: ganttData,
    links: []
  };
};

// --- React Component ---

const BasicGanttChart: React.FC<GanttChartProps> = ({
  trips,
  pendingTripIds = [],
  initialDate = new Date(),
  initialViewMode = 'day',
  onPlannerDateChange,
  onCancelTrip,
  onVisibleTripsChange,
  onVisibleWeeklyTripsChange,
  onAssignCar
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTaskIdRef = useRef<string | null>(null);
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [viewMode, setViewMode] = useState<'day' | 'week'>(initialViewMode);
  const [hoverTooltip, setHoverTooltip] = useState<{
    x: number;
    y: number;
    // title: string;
    lines: string[];
  } | null>(null);

  useEffect(() => {
    window.handleGanttCancelTrip = (tripId: number) => onCancelTrip?.(tripId);
    window.handleGanttAssignCar = (tripId: number) => onAssignCar?.(tripId);

    return () => {
      delete window.handleGanttCancelTrip;
      delete window.handleGanttAssignCar;
    };
  }, [onCancelTrip, onAssignCar]);

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const formatTooltipDate = (date?: Date | string) => {
    if (!date) return '';
    const parsed = typeof date === 'string' ? new Date(date) : date;
    return parsed.toLocaleString('pt-PT', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getGanttTaskTooltip = (task: GanttTask) => {
    if (!task) return null;
    const lines: string[] = [];
    // const title = task.carPlate
    //   ? `Carro: ${task.carPlate}`
    //   : `Pedido: ${task.text}`;

    if (task.passenger) {
      lines.push(`Pessoas: ${task.passenger}`);
    }

    if (task.text) {
      lines.push(`Local: ${task.text}`);
    }

    if (task.start_date && task.end_date) {
      lines.push(
        `Horário: ${formatTooltipDate(task.start_date)} - ${formatTooltipDate(task.end_date)}`
      );
    }

    return { /*title,*/ lines };
  };

  const handleMouseMove = (event: MouseEvent) => {
    const container = containerRef.current;
    if (!container) return;

    const target = event.target as HTMLElement;
    const taskElement = target.closest('[data-task-id]') as HTMLElement | null;
    if (!taskElement) {
      hoverTaskIdRef.current = null;
      setHoverTooltip(null);
      return;
    }

    const taskId = taskElement.getAttribute('data-task-id');
    if (!taskId) {
      hoverTaskIdRef.current = null;
      setHoverTooltip(null);
      return;
    }

    const bounds = container.getBoundingClientRect();
    const tooltipWidth = 280;
    const offset = 12;
    const rawX = event.clientX - bounds.left + offset;
    const overflowRight = rawX + tooltipWidth > bounds.width;
    const x = overflowRight
      ? Math.max(event.clientX - bounds.left - tooltipWidth - offset, offset)
      : rawX;
    const y = event.clientY - bounds.top + offset;
    const position = { x, y };

    if (hoverTaskIdRef.current === taskId) {
      setHoverTooltip(prev => (prev ? { ...prev, ...position } : prev));
      return;
    }

    const task = gantt.getTask(taskId) as GanttTask | null;
    if (!task || task.type === 'project') {
      hoverTaskIdRef.current = null;
      setHoverTooltip(null);
      return;
    }

    hoverTaskIdRef.current = taskId;
    const tooltip = getGanttTaskTooltip(task);
    if (!tooltip) {
      setHoverTooltip(null);
      return;
    }

    setHoverTooltip({
      ...tooltip,
      ...position
    });
  };

  const handleMouseLeave = () => {
    hoverTaskIdRef.current = null;
    setHoverTooltip(null);
  };

  const goToNext = () => {
    setCurrentDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + (viewMode === 'day' ? 1 : 7));

      while (isWeekend(next)) {
        next.setDate(next.getDate() + 1);
      }

      return next;
    });
  };

  const goToPrevious = () => {
    setCurrentDate(prev => {
      const previous = new Date(prev);
      previous.setDate(previous.getDate() - (viewMode === 'day' ? 1 : 7));

      while (isWeekend(previous)) {
        previous.setDate(previous.getDate() - 1);
      }

      return previous;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isSameCalendarDay = (dateA: Date, dateB: Date) => {
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  };

  const isSameCalendarWeek = (dateA: Date, dateB: Date) => {
    const startOfWeek = (date: Date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      const day = d.getDay();
      const diff = (day + 6) % 7; // Monday-based week start
      d.setDate(d.getDate() - diff);
      return d;
    };

    const startA = startOfWeek(dateA).getTime();
    const startB = startOfWeek(dateB).getTime();
    return startA === startB;
  };

  useEffect(() => {
    onPlannerDateChange?.(new Date(currentDate));
  }, [currentDate, onPlannerDateChange]);

  // 1. Static Configuration and Initialization (Runs only once)
  useEffect(() => {
    if (!containerRef.current) return;

    // --- Configuration (Static) ---
    gantt.plugins({
      quick_info: false,
      drag_timeline: true
    });
    // gantt.config.show_quick_info = true;

    gantt.config.scale_height = 50;
    gantt.config.row_height = 45;
    gantt.config.bar_height = 35;

    gantt.config.duration_unit = 'minute';
    gantt.config.duration_step = 1;
    gantt.config.date_format = '%Y-%m-%d %H:%i';

    gantt.config.readonly = true;
    gantt.config.open_tree_initially = true;

    gantt.config.show_links = false;

    gantt.i18n.setLocale('pt');

    // --- Template Functions (Static and Type-Safe) ---
    gantt.attachEvent('onTaskClick', function (id: string) {
      // const task = gantt.getTask(id);

      const replaceId = parseInt(id);
      onAssignCar?.(replaceId);

      // 3. Return false to prevent the default Gantt selection/behavior
      return false;
    });

    // quick_info_content template to add the Cancel/Assign button
    // gantt.templates.quick_info_content = (
    //   start: Date,
    //   end: Date,
    //   task: GanttTask
    // ) => {
    //   const formatTime = gantt.date.date_to_str('%H:%i');

    //   let content = `
    //     <div style="padding: 8px;">
    //       <b>Destinação:</b> ${task.text}<br/>
    //       <b>Passageiros:</b> ${task.passenger}<br/>
    //       <b>Carro:</b> ${task.carPlate || 'N/A'}<br/>
    //   `;

    //   if (task.carPlate && !task.isConfirmed) {
    //     content += `<div style="color: #ff9800; font-weight: bold; margin: 8px 0;">⚠️ A espera de confirmação </div>`;
    //   } else if (!task.carPlate) {
    //     // Unassigned request
    //     content += `<div style="color: #f44336; font-weight: bold; margin: 8px 0;">🔴 Não atribuido </div>`;
    //   }

    //   // if (task.isClipped && task.originalStart && task.originalEnd) {
    //   //   content += `
    //   //     <b>Original Duration:</b> ${formatDateTime(task.originalStart)} - ${formatDateTime(task.originalEnd)}<br/>
    //   //     <b>Visible Today:</b> ${formatTime(start)} - ${formatTime(end)}<br/>
    //   //     <span style="color: #ff9800; font-weight: bold;">⚠️ Multi-day trip</span>
    //   //   `;
    //   // } else {
    //   content += `<b>Horas:</b> ${formatTime(start)} - ${formatTime(end)}`;
    //   // }

    //   content += `</div>`;

    //   content += `<div class="gantt_quick_info_content">`;

    //   // Only show buttons for non-project tasks
    //   if (task.type !== 'project') {
    //     // 1. Assign Car Button (for unassigned tasks)
    //     if (onAssignCar && !task.isConfirmed) {
    //       content += `
    //         <div>
    //           <button
    //             class="btn btn-primary btn-sm"
    //             onclick="window.handleGanttAssignCar('${task.id}')"
    //             style="width: 100%; background-color: #4CAF50; border-color: #4CAF50;"
    //           >
    //             Atribuir um carro
    //           </button>
    //         </div>
    //       `;
    //     }

    //     // 2. Cancel Trip Button (for pending tasks)
    //     if (task.carPlate && !task.isConfirmed && onCancelTrip) {
    //       content += `
    //         <div style="margin-top: 10px;">
    //           <button
    //             class="btn btn-danger btn-sm"
    //             onclick="window.handleGanttCancelTrip('${task.id}')"
    //             style="width: 100%;"
    //           >
    //             Anular pedido
    //           </button>
    //         </div>
    //       `;
    //     }
    //   }

    //   content += `</div>`;
    //   return content;
    // };

    // top left corner
    gantt.config.columns = [
      {
        name: 'text',
        label: 'Passageiros',
        align: 'left',
        // max_width: 175,
        width: 95,
        template: (task: GanttTask) => {
          return `
            <div style="
              padding: 2px;
              white-space: normal;"
            >
              <strong style="display: block; line-height: 1.55;">${task.text}</strong>
            </div>`;
        }
      }
    ];

    // NOTE: task_text template is dynamic and will be re-applied in the second useEffect
    // to capture the correct view boundaries.
    gantt.templates.task_class = (start: Date, end: Date, task: GanttTask) => {
      const classes = [];

      if (task.type === 'project') {
        classes.push('hide-project-bar');
        return classes.join(' ');
      }

      if (!task.carPlate) classes.push('unassigned-trip');
      if (task.status && !task.statusToSet) classes.push(`${task.status}-trip`);
      if (!task.isConfirmed && task.statusToSet)
        classes.push(`${task.statusToSet}-pending-trip`);
      if (task.status && task.statusToSet === 'Pendente')
        classes.push(`pending-trip`);
      // // if (task.isClipped) classes.push('clipped-trip');
      return classes.join(' ');
    };

    // Initialize Gantt on the container
    gantt.init(containerRef.current);

    containerRef.current.addEventListener('mousemove', handleMouseMove);
    containerRef.current.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('mousemove', handleMouseMove);
        containerRef.current.removeEventListener(
          'mouseleave',
          handleMouseLeave
        );
      }
      // Cleanup is necessary for the global gantt instance
      gantt.clearAll();
    };
  }, [onCancelTrip, onAssignCar]);

  const generateSkeletons = (count = 10) => {
    const weekStart = gantt.date.week_start(new Date());

    return Array.from({ length: count }, (_, i) => ({
      id: `skeleton_${i}`,
      text: '', // Empty text
      passenger: '',
      start_date: weekStart,
      duration: 1440 * 5,
      is_skeleton: true // Custom flag for our CSS
    }));
  };

  // 2. Dynamic Data Update (Runs on state change)
  useEffect(() => {
    // This hook runs AFTER the static config hook.

    // --- Dynamic Configuration (Work Time and Scales) ---
    let filteredTrips: (Trip | null)[];
    let startDate: Date;
    let endDate: Date;

    if (viewMode === 'day') {
      // Enable work time for day view
      gantt.config.work_time = true;
      gantt.setWorkTime({ hours: WORK_HOURS });

      const dayStart = new Date(currentDate);
      dayStart.setHours(DAY_START_HOUR, 0, 0, 0);
      const dayEnd = new Date(currentDate);
      dayEnd.setHours(DAY_END_HOUR, 0, 0, 0);
      startDate = dayStart;
      endDate = dayEnd;
      filteredTrips = trips.map(trip => clipTrip(trip, dayStart, dayEnd));
      gantt.config.scales = [
        { unit: 'day', step: 1, format: '%D, %d %M' },
        { unit: 'hour', step: 1, format: '%H:%i' }
      ];
    } else {
      // Disable work time for week view (full 24h)
      gantt.config.work_time = false;

      // Week View: 00:00 to 24:00
      const { startDate: weekStart } = getPlannerWeekRange(currentDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + WEEK_DAYS_TO_SHOW); // Go to the day AFTER Friday (Saturday)
      weekEnd.setHours(WEEK_START_HOUR, 0, 0, 0); // Set to Saturday 00:00
      startDate = weekStart;
      endDate = weekEnd;
      filteredTrips = trips.map(trip => clipTrip(trip, weekStart, weekEnd));
      gantt.config.scales = [{ unit: 'day', step: 1, format: '%D, %d %M' }];
    }

    gantt.config.start_date = startDate;
    gantt.config.end_date = endDate;

    // Re-apply task_text template to capture new currentDate and viewMode boundaries
    gantt.templates.task_text = (start: Date, end: Date, task: GanttTask) => {
      // Prevent task_text from running on project/parent tasks
      if (task.type === 'project') {
        return '';
      }

      // Display passenger and destination
      return `
        <div style="
          padding-inline: 8px;
          white-space: nowrap;
          "
        >
        ${task.id} - ${task.text}
      </div>`;
    };

    // --- Data Parsing ---
    const finalTrips = filteredTrips.filter(
      (trip): trip is Trip => trip !== null
    );
    onVisibleTripsChange?.(finalTrips.map(trip => Number(trip.id)));

    const { startDate: weeklyStart } = getPlannerWeekRange(currentDate);
    const weeklyEnd = new Date(weeklyStart);
    weeklyEnd.setDate(weeklyEnd.getDate() + WEEK_DAYS_TO_SHOW);
    weeklyEnd.setHours(0, 0, 0, 0);
    const weeklyTrips = trips
      .map(trip => clipTrip(trip, weeklyStart, weeklyEnd))
      .filter((trip): trip is Trip => trip !== null);
    onVisibleWeeklyTripsChange?.(weeklyTrips.map(trip => Number(trip.id)));

    const pendingIdsSet = new Set(pendingTripIds);
    const ganttData = transformTripsToGanttData(finalTrips, pendingIdsSet);

    // Update data and render
    gantt.clearAll();
    // gantt.parse(ganttData);
    if (!trips) {
      gantt.parse({ data: generateSkeletons() });
    } else {
      gantt.parse(ganttData);
    }
    gantt.render();

    gantt.resetLayout();
  }, [
    currentDate,
    viewMode,
    trips,
    pendingTripIds,
    onVisibleTripsChange,
    onVisibleWeeklyTripsChange
  ]);

  return (
    <div>
      <div className="gantt-chart-button-container">
        <div className="selection-buttons-container">
          <ButtonGroup className="item-content">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={goToPrevious}
              // style={{ marginInline: '5px' }}
            >
              {/* Previous {viewMode === 'day' ? 'Day' : 'Week'} */}
              <FeatherIcon icon="chevron-left" size={16} />
            </Button>
            <Button
              className="middle-button"
              variant="outline-primary"
              size="sm"
              onClick={goToToday}
              // style={{ marginInline: '5px' }}
            >
              {viewMode === 'day'
                ? isSameCalendarDay(currentDate, new Date())
                  ? 'Dia atual'
                  : 'Ir para Hoje'
                : isSameCalendarWeek(currentDate, new Date())
                  ? 'Semana atual'
                  : 'Ir para semana atual'}
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={goToNext}
              // style={{ marginInline: '5px' }}
            >
              {/* Next {viewMode === 'day' ? 'Day' : 'Week'} */}
              <FeatherIcon icon="chevron-right" size={16} />
            </Button>
          </ButtonGroup>
          <ButtonGroup className="item-content">
            {/* <Button
              variant="primary"
              size="sm"
              onClick={() =>
                viewMode === 'day' ? setViewMode('week') : setViewMode('day')
              }
              // className="btn btn-primary btn-sm"
            >
              Plano {viewMode === 'day' ? 'diário' : 'semanal'}
              Plano diário
            </Button> */}
            <Button
              variant={viewMode === 'day' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('day')}
              // className="btn btn-primary btn-sm"
            >
              Plano diário
            </Button>
            <Button
              variant={viewMode === 'day' ? 'outline-primary' : 'primary'}
              // size="sm"
              // style={{ marginInline: '5px' }}
              onClick={() => setViewMode('week')}
              // className="btn btn-primary btn-sm"
            >
              Plano semanal
            </Button>
          </ButtonGroup>
        </div>
      </div>
      <style>{`
        .hide-project-bar .gantt_task_content {
          display: none;
        }
        .gantt_task_line.hide-project-bar {
          display: none !important;
        }
        .gantt_task_line {
          background-color: #777777;
          border: none;
          border-radius: 0px;
        }
        .gantt_task_line.unassigned-trip {
          background: linear-gradient(135deg, #777777 25%, #5f5f5f 100%);
          border: none;
          opacity: 0.8;
        }
        .gantt_task_line.pending-trip {
          background: linear-gradient(135deg, #ff6f00 25%, #5f5f5f 100%);
          border: none;
          opacity: 0.9;
        }
        .gantt_task_line.Aprovado-pending-trip {
          background: linear-gradient(135deg, #4CAF50 25%, #5f5f5f 100%);
          border: none;
          opacity: 0.8;
        }
        .gantt_task_line.Aprovado-trip {
          background: #4CAF50;
          border: none;
          opacity: 0.8;
        }
        .gantt_task_line.Recusado-pending-trip {
          background: linear-gradient(135deg, #f44336 25%, #5f5f5f 100%);
          border: none;
          opacity: 0.8;
        }
        .gantt_task_line.Recusado-trip {
          background: #f44336;
          border: none;
          opacity: 0.8;
        }
        .gantt_tree_indent {
          width: 0px !important;
        }
        .gantt_task_cell.week_end, .gantt_task_cell.no_work_hour {
          background-color: var(--dhx-gantt-base-colors-background-alt);
        }
        .gantt-tooltip {
          position: absolute;
          z-index: 1050;
          max-width: 280px;
          background: rgba(33, 33, 33, 0.95);
          color: white;
          border-radius: 6px;
          padding: 10px 12px;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
          font-size: 12px;
          line-height: 1.4;
          pointer-events: none;
        }
        .gantt-tooltip-title {
          font-weight: 700;
          margin-bottom: 6px;
        }
      `}</style>
      <div style={{ position: 'relative' }}>
        {hoverTooltip && (
          <div
            className="gantt-tooltip"
            style={{ top: hoverTooltip.y, left: hoverTooltip.x }}
          >
            {/* <div className="gantt-tooltip-title">{hoverTooltip.title}</div> */}
            {hoverTooltip.lines.map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </div>
        )}
        <div
          ref={containerRef}
          style={{ height: '400px', width: '100%', border: '1px solid #ccc' }}
        />
      </div>
    </div>
  );
};

export default BasicGanttChart;

// .gantt_task_line.clipped-trip.pending-trip {
//   background: linear-gradient(135deg, #ff6f00 0%, #e65100 100%);
//   border: none;
// }
// .gantt_task_line.clipped-trip {
//   background: linear-gradient(135deg, #4CAF50 0%, #3f8F50 100%);
//   border: none;
// }
